// @flow

import React, { useEffect, useRef, useState } from 'react';
import { withRouter, type Match, type RouterHistory } from 'react-router';
import { connect } from 'react-redux';
import config from 'main-app/config';
import { i18n } from 'shared/utils';
import * as Actions from 'main-app/store/Actions';
import Logo from 'main-app/components/PageHeaderLogo/img/logo.svg';
// Imported from the pre-bundled CJS build (rather than the top-level `lucide-react`
// ESM barrel, and rather than the per-icon `esm/icons/*.mjs` files) for two reasons:
// 1. The ESM barrel pulls in all 2000+ icons, which stalls Webpack 4 dev builds.
// 2. The per-icon `.mjs` files themselves use named ESM imports from `react`
//    (`import { createElement, forwardRef } from 'react'`), which Webpack 4 can't
//    interop against React's CommonJS build ("Can't import the named export... from
//    non EcmaScript module"). The single `dist/cjs/lucide-react.cjs` file is genuine
//    CommonJS (`exports.IconName = ...`), so it doesn't hit that interop issue, while
//    still being a single already-bundled module for webpack to parse.
import {
  LayoutDashboard,
  BarChart3,
  TrendingUp,
  ShieldCheck,
  ClipboardList,
  Ticket,
  CalendarClock,
  Truck,
  Receipt,
  PackageMinus,
  Undo2,
  Box,
  ListTree,
  Hash,
  AlertTriangle,
  MapPin,
  Building2,
  ShoppingCart,
  ClipboardCheck,
  History,
  ListChecks,
  FileClock,
  Users,
  Landmark,
  BookOpen,
  ScrollText,
  Link2,
  Settings as SettingsIcon,
  ChevronDown,
  ChevronRight,
} from 'lucide-react/dist/cjs/lucide-react.cjs';
import {
  Wrapper,
  TopArea,
  Header,
  ScrollArea,
  MenuList,
  MenuGroupHeader,
  MenuGroupContent,
  BottomBar,
} from './styled';
import MenuItem from './MenuItem';

const NAV_ICON_SIZE = 20;
const GROUP_ICON_SIZE = 16;
const GROUP_ICON_COLOR = '#1A1A1A';

type Props = {
  match: Match,
  history: RouterHistory,
  mainMenuOpen: boolean,
  qcIntegrationEnabled?: boolean,
};

const Menu = ({
  history,
  mainMenuOpen,
  qcIntegrationEnabled,
  match: { path },
}: Props) => {
  const [expandedGroups, setExpandedGroups] = useState(() => {
    // Initialize from localStorage or default to all expanded
    const saved = localStorage.getItem('menuExpandedGroups');
    if (saved) {
      try {
        return JSON.parse(saved);
      } catch (e) {
        return {
          production: true,
          inventory: true,
          procurement: true,
          customers: true,
          finance: true,
        };
      }
    }
    return {
      production: true,
      inventory: true,
      procurement: true,
      customers: true,
      finance: true,
    };
  });

  const toggleGroup = (groupKey: string) => {
    setExpandedGroups(prev => {
      const newState = {
        ...prev,
        [groupKey]: !prev[groupKey],
      };
      // Save to localStorage
      localStorage.setItem('menuExpandedGroups', JSON.stringify(newState));
      return newState;
    });
  };

  const getMainMenuItems = () => {
    return {
      top: [
        {
          key: 'menu-item-dashboard',
          icon: <LayoutDashboard size={NAV_ICON_SIZE} />,
          title: i18n.t('Dashboard'),
          linkTo: '/dashboard',
          active: path === '/' || path.startsWith('/dashboard'),
        },
        {
          key: 'menu-item-reports',
          icon: <BarChart3 size={NAV_ICON_SIZE} />,
          title: i18n.t('Reports'),
          linkTo: '/reports',
          active: path === '/' || path.startsWith('/report'),
        },
        {
          key: 'menu-item-sales-analytics',
          icon: <TrendingUp size={NAV_ICON_SIZE} />,
          title: i18n.t('Sales Analytics'),
          linkTo: '/sales-analytics',
          active: path.startsWith('/sales-analytics'),
        },
        // Only shown once a QC API key has been configured under Settings > Integrations > QC
        ...(qcIntegrationEnabled
          ? [
              {
                key: 'menu-item-qc',
                icon: <ShieldCheck size={NAV_ICON_SIZE} />,
                title: i18n.t('QC'),
                linkTo: '/qc',
                active: path.startsWith('/qc'),
              },
            ]
          : []),
      ],
      groups: {
        production: {
          title: i18n.t('Production'),
          items: [
            {
              key: 'menu-item-orders',
              icon: <ClipboardList size={NAV_ICON_SIZE} />,
              title: i18n.t('Orders'),
              linkTo: '/orders',
              active: path.startsWith('/order'),
            },
            {
              key: 'menu-item-jobs',
              icon: <Ticket size={NAV_ICON_SIZE} />,
              title: i18n.t('Job Tickets'),
              linkTo: '/jobs',
              active: path.startsWith('/job'),
            },
            {
              key: 'menu-item-production-schedule',
              icon: <CalendarClock size={NAV_ICON_SIZE} />,
              title: i18n.t('Production Schedule'),
              linkTo: '/production-schedule',
              active: path.startsWith('/production-schedule'),
            },
            {
              key: 'menu-item-shipments',
              icon: <Truck size={NAV_ICON_SIZE} />,
              title: i18n.t('Shipments'),
              linkTo: '/shipments',
              active: path.startsWith('/shipment'),
            },
            {
              key: 'menu-item-invoices',
              icon: <Receipt size={NAV_ICON_SIZE} />,
              title: i18n.t('Invoices'),
              linkTo: '/invoices',
              active: path.startsWith('/invoice'),
            },
            {
              key: 'menu-item-backorders',
              icon: <PackageMinus size={NAV_ICON_SIZE} />,
              title: i18n.t('Backorders'),
              linkTo: '/backorders',
              active: path.startsWith('/backorder'),
            },
            {
              key: 'menu-item-returns',
              icon: <Undo2 size={NAV_ICON_SIZE} />,
              title: i18n.t('Returns'),
              linkTo: '/returns',
              active: path.startsWith('/return'),
            },
          ],
        },
        inventory: {
          title: i18n.t('Inventory'),
          items: [
            {
              key: 'menu-item-inventory',
              icon: <Box size={NAV_ICON_SIZE} />,
              title: i18n.t('Items'),
              linkTo: '/items',
              active:
                (path.startsWith('/item') || path === '/items') &&
                !path.startsWith('/bom-inquiry'),
            },
            {
              key: 'menu-item-bom-inquiry',
              icon: <ListTree size={NAV_ICON_SIZE} />,
              title: i18n.t('BOM Inquiry'),
              linkTo: '/bom-inquiry',
              active: path.startsWith('/bom-inquiry'),
            },
            {
              key: 'menu-item-lots',
              icon: <Hash size={NAV_ICON_SIZE} />,
              title: i18n.t('Lots'),
              linkTo: '/lots',
              active: path.startsWith('/lot'),
            },
            {
              key: 'menu-item-low-stock',
              icon: <AlertTriangle size={NAV_ICON_SIZE} />,
              title: i18n.t('Low Stock Items'),
              linkTo: '/low-stock-items',
              active: path.startsWith('/low-stock-items'),
            },
            {
              key: 'menu-item-warehouse-locations',
              icon: <MapPin size={NAV_ICON_SIZE} />,
              title: i18n.t('Locations'),
              linkTo: '/warehouse-locations',
              active: path.startsWith('/warehouse-locations'),
            },
          ],
        },
        procurement: {
          title: i18n.t('Procurement'),
          items: [
            {
              key: 'menu-item-vendors',
              icon: <Building2 size={NAV_ICON_SIZE} />,
              title: i18n.t('Vendors'),
              linkTo: '/vendors',
              active: path.startsWith('/vendor'),
            },
            {
              key: 'menu-item-purchase-orders',
              icon: <ShoppingCart size={NAV_ICON_SIZE} />,
              title: i18n.t('Purchase Orders'),
              linkTo: '/purchase-orders',
              active: path.startsWith('/purchase-order') && !path.startsWith('/purchase-orders/'),
            },
            {
              key: 'menu-item-po-approvals',
              icon: <ClipboardCheck size={NAV_ICON_SIZE} />,
              title: i18n.t('PO Approvals'),
              linkTo: '/purchase-orders/approvals',
              active: path.startsWith('/purchase-orders/approvals'),
            },
            {
              key: 'menu-item-receipt-history',
              icon: <History size={NAV_ICON_SIZE} />,
              title: i18n.t('Receipt History'),
              linkTo: '/purchase-orders/receipt-history',
              active: path.startsWith('/purchase-orders/receipt-history'),
            },
            {
              key: 'menu-item-material-requirements',
              icon: <ListChecks size={NAV_ICON_SIZE} />,
              title: i18n.t('Material Requirements'),
              linkTo: '/material-requirements',
              active: path.startsWith('/material-requirements'),
            },
            {
              key: 'menu-item-po-generation-history',
              icon: <FileClock size={NAV_ICON_SIZE} />,
              title: i18n.t('PO Generation History'),
              linkTo: '/purchase-orders/generation-history',
              active: path.startsWith('/purchase-orders/generation-history'),
            },
          ],
        },
        customers: {
          title: i18n.t('Customers'),
          items: [
            {
              key: 'menu-item-customers',
              icon: <Users size={NAV_ICON_SIZE} />,
              title: i18n.t('Customers'),
              linkTo: '/customers',
              active: path.startsWith('/customer'),
            },
          ],
        },
        finance: {
          title: i18n.t('Finance'),
          items: [
            {
              key: 'menu-item-finance-dashboard',
              icon: <Landmark size={NAV_ICON_SIZE} />,
              title: i18n.t('Finance Dashboard'),
              linkTo: '/finance-dashboard',
              active: path.startsWith('/finance-dashboard'),
            },
            {
              key: 'menu-item-chart-of-accounts',
              icon: <BookOpen size={NAV_ICON_SIZE} />,
              title: i18n.t('Chart of Accounts'),
              linkTo: '/chart-of-accounts',
              active: path.startsWith('/chart-of-accounts'),
            },
            {
              key: 'menu-item-journal-entries',
              icon: <ScrollText size={NAV_ICON_SIZE} />,
              title: i18n.t('Journal Entries'),
              linkTo: '/journal-entries',
              active: path.startsWith('/journal-entries'),
            },
            {
              key: 'menu-item-account-mappings',
              icon: <Link2 size={NAV_ICON_SIZE} />,
              title: i18n.t('Account Mappings'),
              linkTo: '/account-mappings',
              active: path.startsWith('/account-mappings'),
            },
          ],
        },
      },
      bottom: [
        {
          key: 'menu-item-settings',
          icon: <SettingsIcon size={NAV_ICON_SIZE} />,
          title: i18n.t('Settings'),
          linkTo: '/settings',
          active: path.startsWith('/settings'),
        },
      ],
    };
  };

  const handleMenuClickAway = (e: Event) => {
    if (mainMenuOpen && wrapperRef && !wrapperRef.contains(e.target)) {
      Actions.toggleMainMenu(false);
    }
  };

  const handleGoTo = (path: string, e: Event) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }

    if (mainMenuOpen) {
      Actions.toggleMainMenu(false);
    }

    setTimeout(() => {
      history.push(path);
    }, 0);
  };

  const renderMenuList = (list: Array<Object>) => {
    const whitelistedList =
      !config.blacklistedMenuItems || !config.blacklistedMenuItems.length
        ? list
        : list.filter(
            menuItem => !config.blacklistedMenuItems.includes(menuItem.key),
          );

    return (
      <>
        {whitelistedList.map((menuItemProps, i) => (
          <MenuItem
            key={menuItemProps.key}
            onClick={e => handleGoTo(menuItemProps.linkTo, e)}
            {...menuItemProps}
          />
        ))}
      </>
    );
  };

  const renderMenuGroup = (groupKey: string, group: Object) => {
    const isExpanded = expandedGroups[groupKey];
    
    return (
      <div key={groupKey}>
        <MenuGroupHeader onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          toggleGroup(groupKey);
        }}>
          <span>{group.title}</span>
          {isExpanded ? (
            <ChevronDown size={GROUP_ICON_SIZE} color={GROUP_ICON_COLOR} />
          ) : (
            <ChevronRight size={GROUP_ICON_SIZE} color={GROUP_ICON_COLOR} />
          )}
        </MenuGroupHeader>
        {isExpanded && (
          <MenuGroupContent onClick={(e) => e.stopPropagation()}>
            {renderMenuList(group.items)}
          </MenuGroupContent>
        )}
      </div>
    );
  };

  /**
   * By default, useEffect runs after every render
   * The returned value from useEffect is used to cleanup the effect, similar to componentWillUnmount
   * The second arg is an array of values that the effect depends on
   * If there are changes to those values between renders, then the effect will fire, similar to componentDidUpdate
   * If the second arg is an empty array, then the effect only fires once after layout and paint, sort of similar to componentDidMount
   */
  useEffect(() => {
    document.addEventListener('mousedown', handleMenuClickAway);
    document.addEventListener('touchend', handleMenuClickAway);

    return () => {
      document.removeEventListener('mousedown', handleMenuClickAway);
      document.removeEventListener('touchend', handleMenuClickAway);
    };
  }, []);

  const menuItems = getMainMenuItems();
  const wrapperRef: any = useRef(null);

  return (
    <Wrapper show={mainMenuOpen} ref={wrapperRef}>
      <TopArea>
        <Header><Logo /></Header>
      </TopArea>
      <ScrollArea>
        <MenuList>
          {renderMenuList(menuItems.top)}
          {Object.keys(menuItems.groups).map(groupKey =>
            renderMenuGroup(groupKey, menuItems.groups[groupKey])
          )}
        </MenuList>
      </ScrollArea>
      <BottomBar>
        {renderMenuList(menuItems.bottom)}
      </BottomBar>
    </Wrapper>
  );
};

// $FlowFixMe
export default withRouter(
  connect(state => ({
    mainMenuOpen: state.mainMenuOpen,
  }))(Menu),
);
